import type React from "react";
import {useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField} from "@mui/material";

type TaxonomyRenameCategoryDialogProps = {
    from: string;
    to: string;
    /** The category's current description; the field starts with it. */
    description: string;
    isSaving: boolean;
    onCancel: () => void;
    onConfirm: (description: string) => void;
};

/**
 * Confirms a rename typed on a tree card ("from" → "to") and lets the user change the description
 * with it, as the current builder's "Update category" dialog does. The description is optional.
 * With `to` equal to `from` it only edits the description. Mount it fresh for each rename, so the
 * field starts with the current description.
 */
export const TaxonomyRenameCategoryDialog: React.FC<TaxonomyRenameCategoryDialogProps> = ({
                                                                                              from,
                                                                                              to,
                                                                                              description: initialDescription,
                                                                                              isSaving,
                                                                                              onCancel,
                                                                                              onConfirm,
                                                                                          }) => {
    const [description, setDescription] = useState(initialDescription);
    const descriptionOnly = from === to;

    return (
        <Dialog open onClose={isSaving ? undefined : onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>{descriptionOnly ? 'Edit description' : 'Rename category'}</DialogTitle>
            <DialogContent>
                <Stack gap={2}>
                    <DialogContentText>
                        {descriptionOnly
                            ? <>Description of <Box component="strong" sx={{color: 'text.primary'}}>{from}</Box></>
                            : <>
                                Do you want to change <Box component="strong" sx={{color: 'text.primary'}}>{from}</Box>
                                {' '}to <Box component="strong" sx={{color: 'text.primary'}}>{to}</Box>?
                            </>}
                    </DialogContentText>
                    <TextField
                        label="Description"
                        placeholder="What belongs in this category (optional)"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        size="small"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
                <Button variant="contained" onClick={() => onConfirm(description.trim())} loading={isSaving} autoFocus>
                    {descriptionOnly ? 'Save' : 'Change'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
